import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useSearch } from '@tanstack/react-router'
import { useAtom } from 'jotai'
import { Loader } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useLogin } from '@/gen/publicApi'

import { Button } from '@/components/button'
import { Form } from '@/components/form'
import FormInput from '@/components/form/formInput'
import { useToast } from '@/components/toast/useToast'

import { authTokenAtom } from '@/state/authToken'

const LoginForm = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setAuthToken] = useAtom(authTokenAtom)
    const { redirect } = useSearch({
        from: '/login/',
    })
    const { toast } = useToast()

    const formSchema = z.object({
        email: z.string().email(),
        password: z.string().min(1, 'Required'),
    })

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        mode: 'onBlur',
    })

    const loginMutation = useLogin({
        mutation: {
            onSuccess: data => {
                setAuthToken(data.token!)
            },
            onError: error => {
                console.error('login error', error)
                setAuthToken(undefined)
                toast({
                    variant: 'destructive',
                    title: 'Login failed!',
                    description: `Login failed, please try again`,
                })
            },
        },
    })

    const onSubmit = (values: z.infer<typeof formSchema>) => {
        loginMutation.mutate(values)
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="pt-4 space-y-4">
                <FormInput control={form.control} name="email" label={<>Email</>} placeholder="you@example.com" />
                <FormInput control={form.control} name="password" type="password" label={<>Password</>} />
                <div className="flex items-center justify-between">
                    <Button type="submit" disabled={loginMutation.isPending}>
                        {loginMutation.isPending && <Loader className="w-4 h-4 mr-2 animate-spin" />}
                        Login
                    </Button>
                    <Link to={`/signup`} search={{ redirect }} className="text-sm">
                        Or create an account
                    </Link>
                </div>
                <div className="flex items-center justify-between">
                    <Link to={`/forgot-password`} className="text-sm">
                        Forgot password?
                    </Link>
                </div>
            </form>
        </Form>
    )
}

export default LoginForm
